import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import HeroSection from './components/HeroSection';
import StorySection from './components/StorySection';
import PhilosophySection from './components/PhilosophySection';
import SkillsSection from './components/SkillsSection';
import ProcessSection from './components/ProcessSection';
import PersonalSection from './components/PersonalSection';
import CTASection from './components/CTASection';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>About MR MIKE - Full-Stack Developer | Mr Mike Technologies</title>
        <meta
          name="description"
          content="Learn about Oforka Michael Chinonso (MR MIKE), a production-ready full-stack developer who bridges technical excellence with business acumen. Discover my journey, philosophy, and approach to building scalable solutions at Mr Mike Technologies."
        />
        <meta
          name="keywords"
          content="Mr Mike Technologies, full-stack developer, React developer, Node.js developer, web development, software engineer, technical consultant, startup developer, production-ready solutions"
        />
        <meta property="og:title" content="About MR MIKE - Full-Stack Developer | Mr Mike Technologies" />
        <meta
          property="og:description"
          content="Production-ready problem solver bridging technical excellence with business acumen. Building scalable solutions for growth-stage startups and enterprise clients at Mr Mike Technologies."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://mikedev.pro/about" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
            <Breadcrumbs />
          </div>

          {/* --- Profile Image Section --- */}
          <section className="relative flex flex-col md:flex-row items-center justify-center gap-10 my-14 py-14 px-4 md:px-20 rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900/90 via-blue-900/80 to-slate-800/90 shadow-2xl border border-blue-800/30 backdrop-blur-xl">
            {/* Animated Glow Background */}
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[80vw] h-72 bg-gradient-to-r from-cyan-500/20 via-blue-500/10 to-purple-600/20 blur-3xl rounded-full opacity-60 pointer-events-none animate-pulse-slow"></div>
            <div className="absolute bottom-0 right-0 w-72 h-72 bg-gradient-to-tr from-purple-500/20 via-blue-400/10 to-cyan-400/20 blur-2xl rounded-full opacity-50 pointer-events-none"></div>

            {/* Profile Image with Badge */}
            <div className="relative flex-shrink-0 w-full md:w-1/3 flex justify-center">
              <div className="relative group">
                <img
                  src="/assets/images/my-pic6.png"
                  alt="Mr Mike Technologies"
                  className="rounded-2xl shadow-2xl max-w-xs md:max-w-sm border-4 border-cyan-500/60 group-hover:scale-105 transition-transform duration-300"
                />
                {/* Badge */}
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-600 to-blue-700 text-white text-xs md:text-sm font-semibold px-4 py-1 rounded-full shadow-lg border border-cyan-400/40 backdrop-blur-md">
                  Full-Stack Developer
                </span>
              </div>
            </div>

            {/* Text Content */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left w-full md:w-2/3">
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-xl tracking-tight">
                Meet <span className="text-cyan-400">Mr Mike Technologies</span>
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 rounded-full mb-6"></div>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-2xl font-medium leading-relaxed">
                Passionate about building <span className="text-cyan-300 font-semibold">scalable, production-ready solutions</span> that bridge technical excellence with business value.<br />
                With a proven track record in full-stack development, I help startups and enterprises unlock their digital potential.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="bg-cyan-800/60 text-cyan-100 px-4 py-2 rounded-lg text-sm font-semibold shadow">
                  React & Node.js Expert
                </span>
                <span className="bg-blue-800/60 text-blue-100 px-4 py-2 rounded-lg text-sm font-semibold shadow">
                  Cloud & DevOps
                </span>
                <span className="bg-purple-800/60 text-purple-100 px-4 py-2 rounded-lg text-sm font-semibold shadow">
                  Mentor & Consultant
                </span>
              </div>
            </div>
          </section>
          {/* --- End Profile Image Section --- */}

          <HeroSection />
          <StorySection />
          {/* --- Journey Hero Image Section --- */}
          <section className="relative flex items-center justify-center my-14 py-14 px-4 md:px-20 rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-800/95 shadow-2xl border border-blue-800/30 backdrop-blur-xl min-h-[380px] md:min-h-[440px]">
            {/* Animated Glow Background */}
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[80vw] h-72 bg-gradient-to-r from-cyan-500/25 via-blue-500/15 to-purple-600/25 blur-3xl rounded-full opacity-70 pointer-events-none animate-pulse-slow"></div>
            <div className="absolute bottom-0 right-0 w-72 h-72 bg-gradient-to-tr from-purple-500/25 via-blue-400/15 to-cyan-400/25 blur-2xl rounded-full opacity-60 pointer-events-none"></div>
            {/* Subtle grid overlay for tech feel */}
            <div className="absolute inset-0 pointer-events-none z-0">
              <svg className="w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#fff" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
            {/* Overlay for depth */}
            <div className="absolute inset-0 bg-black/40 pointer-events-none z-10" />

            {/* Main Image with animated border */}
            <div className="relative z-20 flex flex-col items-center">
              <div className="relative">
                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 blur-md opacity-60 animate-gradient-x"></div>
                <img
                  src="/assets/images/my-pic2.jpg"
                  alt="Mr Mike Technologies Journey"
                  className="relative rounded-2xl shadow-2xl border-4 border-cyan-500/70 object-cover w-full max-w-lg md:max-w-3xl h-72 md:h-96 mx-auto"
                  style={{ objectPosition: 'center' }}
                />
                {/* Floating Badge */}
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-600 to-blue-700 text-white text-xs md:text-sm font-semibold px-5 py-2 rounded-full shadow-lg border border-cyan-400/40 backdrop-blur-md">
               
                </span>
              </div>
              {/* Glassmorphism Card Overlay */}
              <div className="relative z-20 mt-10 px-6 py-5 bg-white/10 backdrop-blur-lg rounded-xl shadow-lg border border-cyan-400/20 max-w-xl text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-cyan-100 mb-2 tracking-tight drop-shadow">
                  From Curiosity to Craft
                </h3>
                <p className="text-base md:text-lg text-blue-100 font-medium">
                  Every milestone is a story of growth, resilience, and a relentless pursuit of technical and business excellence.
                </p>
              </div>
            </div>
          </section>
          {/* --- End Journey Hero Image Section --- */}
      
          <PhilosophySection />
          <SkillsSection />
          <ProcessSection />
          <PersonalSection />
          <CTASection />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default About;